import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { keys } from '../services/queryKeys'
import { getPartner } from '../services/partnerService'
import { WizardProvider, useWizard } from '../context/WizardContext'
import WizardShell from '../components/wizard/WizardShell'
import StepBody from '../components/wizard/StepBody'
import { Empty, Failed, Loading } from '../components/common/States'
import Button from '../components/common/Button'
import { useDeliveryQuote } from '../hooks/useDeliveryQuote'
import { ACTIONS, MESSAGES } from '../utils/copy'
import { readPrefill } from '../utils/prefill'

/**
 * The order form, one question at a time.
 *
 * Built beside the shipped two-step form rather than over it, and served from its own
 * route until it is finished. Every step can be opened in a browser the day it lands,
 * which is the only way the thing this redesign is actually for — whether an elderly
 * customer can finish it alone — gets checked at all. A wizard assembled entirely behind
 * a flag is a wizard nobody looks at until it is too late to change.
 *
 * At B7 this takes over `/order/:slug` and the old form is deleted.
 */
/** 16px, currentColor, and still when the reader has asked for less motion. */
function Spinner() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="w-4 h-4 animate-spin motion-reduce:animate-none"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    >
      <path d="M12 3a9 9 0 1 0 9 9" />
    </svg>
  )
}

function Wizard({ partner }) {
  const { state, step, isSummary, submit } = useWizard()
  const navigate = useNavigate()

  /**
   * Asked only on the summary, because asking holds a real rider for thirty seconds.
   *
   * Quoting from step 2 would take somebody out of the pool while the customer is still
   * writing their shopping list — and with two riders online in Los Baños, that is half
   * the town's supply spent on a form nobody has finished.
   */
  const delivery = useDeliveryQuote({
    partnerSlug: partner.slug,
    pickup: state.pickup,
    dropoff: state.dropoff,
    active: isSummary,
  })

  /**
   * Placeable only once the exact figure has arrived.
   *
   * This is the promise the whole feature makes: the number on screen is the number
   * charged. A button that works while the fee is still loading would let somebody commit
   * to a blank, which is the situation this replaced.
   */
  const ready = Boolean(delivery.token) && !delivery.unavailable

  /**
   * Guarded here as well as by `disabled`, and not out of caution.
   *
   * A disabled attribute is a hint to a pointer. Enter on a focused button, a double tap
   * that lands as the state flips, or an assistive click all reach this function anyway —
   * and this one places an order and charges money. The check that matters is the one in
   * the handler.
   */
  const place = async () => {
    if (!ready || state.submitting) return

    const order = await submit(delivery.token)

    if (order) {
      navigate(`/order/${partner.slug}/success`, { state: { order }, replace: true })
      return
    }

    /**
     * 409 means the quote lapsed between the tap and the server.
     *
     * Re-asked at once, so the new figure is on screen before the customer looks up — and
     * the amount that changed is named beside it. The next press carries the new token
     * and goes through. Not an error state: nothing is wrong, the price moved.
     */
    delivery.refresh()
  }

  return (
    <>
      <Link
        to={`/partners/${partner.slug}`}
        className="inline-flex items-center min-h-[32px] text-body-s text-ink-3 transition-colors duration-quick hover:text-brand"
      >
        ← {partner.businessName}
      </Link>

      <WizardShell
        footer={isSummary ? (
          <Button
            type="button"
            onClick={place}
            disabled={state.submitting || !ready}
            className="flex-1"
          >
            {/* Spinning while the fee is being found, and while the order is going out.
                A button that is merely greyed reads as broken; one that is visibly working
                reads as busy, and the difference is whether the customer keeps pressing. */}
            {(state.submitting || !ready) && !delivery.unavailable && <Spinner />}

            {state.submitting
              ? ACTIONS.placing
              : (delivery.changed ? ACTIONS.confirm : (ready ? ACTIONS.place : ACTIONS.waiting))}
          </Button>
        ) : null}
      >
        <StepBody step={step} partner={partner} delivery={delivery} />
      </WizardShell>
    </>
  )
}

export default function OrderWizard() {
  const { slug } = useParams()
  const [search] = useSearchParams()

  /**
   * A route handed over by the public price checker.
   *
   * Read here rather than inside the provider so the provider stays a function of its
   * props. It arrives in the query string because that is the only carrier that survives
   * the jump out of Messenger's in-app browser into Chrome that signing in forces — see
   * `utils/prefill.js`, and `returnTo.toPath`, which keeps the query across the sign-in.
   */
  const prefill = readPrefill(search)

  // The same key as the partner detail page, so stepping from there into the form reuses
  // what was already fetched instead of asking again.
  const { isPending: loading, data, error } = useQuery({
    queryKey: keys.partners.detail(slug),
    queryFn: () => getPartner(slug),
  })

  if (loading) return <Loading label={MESSAGES.loadingPartner} />

  if (error) {
    return error.status === 404 ? (
      <Empty
        title={MESSAGES.partnerClosed}
        note={MESSAGES.partnerClosedNote}
        action={
          <Link
            to="/home"
            className="text-body-s font-semibold text-brand underline underline-offset-4"
          >
            {MESSAGES.browse}
          </Link>
        }
      />
    ) : (
      <Failed error={error} />
    )
  }

  return (
    <section className="px-5 pt-4 pb-7">
      {/* Keyed on the slug so switching partners starts a clean form rather than carrying
          the previous one's answers across. */}
      <WizardProvider key={data.data.slug} partnerSlug={data.data.slug} prefill={prefill}>
        <Wizard partner={data.data} />
      </WizardProvider>
    </section>
  )
}
